/**
 * The two questions compare mode asks, as dialogs (owner, 2026-08-12).
 *
 * ## Why a dialog and not the row under the field
 *
 * The provenance suggestion first shipped as a block below the compare input —
 * a lead line, a chip, and a caveat, permanently occupying three lines of the
 * loader bar for something read once and then never again. It pushed the model
 * down the page for every visitor, including the ones who never open compare.
 *
 * A dialog puts the question where a question belongs: in front of you when it
 * is asked, and gone afterwards. The row is now a modal step of the compare
 * flow rather than furniture.
 *
 * ## Why both questions live in one module
 *
 * Opening compare can ask two things in sequence — *which model?* and, if that
 * model turns out to hold several, *which one of it?* They share a shell, a
 * focus contract and a dismissal, and splitting them would mean two dialogs
 * that must look and behave identically by coincidence.
 *
 * ## The accessibility contract, inherited from `report-dialog.ts`
 *
 * Native `<dialog>` supplies modal focus containment and Escape. It does **not**
 * supply focus return — after a click the browser's idea of the previously
 * focused element is usually the body, which drops a keyboard user at the top of
 * the page. So `opener` is required here rather than optional, and the shell
 * removes itself on close so repeated opens cannot accumulate stale subtrees
 * with live listeners.
 *
 * ## The strings are author-controlled
 *
 * Suggested repo ids come from a GGUF header or a README, and `hf/provenance.ts`
 * has already put both through the predicates typed input meets. This module
 * still builds DOM and sets `textContent` rather than assembling HTML: picking
 * a suggestion fills the compare field, and the value then meets the same
 * canonical validation as anything typed there.
 */

import type { RepoComponent } from "../hf/components.js";
import { MAX_BASE_MODEL_CHIPS, type Provenance } from "../hf/provenance.js";
import { renderComponentChoice } from "./component-choice.js";

/** Which instrument spoke, in the visitor's words rather than the type's. */
function sourceLabel(sources: readonly string[]): string {
  const file = sources.includes("file");
  const card = sources.includes("card");
  if (file && card) return "file and model card agree";
  return file ? "declared in the file" : "from the model card";
}

/**
 * The lead-in, which is the sentence the buttons finish.
 *
 * `quantizedBy` earns its place in the line rather than a row of its own: it is
 * the *relation* between this repo and the ones being offered, and the Hub's own
 * `base_model_relation` was null on every repo sampled — so a GGUF saying
 * "Unsloth quantized this" is the only place that relation is ever stated.
 */
function leadFor(provenance: Provenance): string {
  if (provenance.agreement === "disagree") {
    return "Its two sources disagree, so both are offered:";
  }
  const by = provenance.quantizedBy;
  return by !== undefined
    ? `This model was quantized by ${by}, from:`
    : "This model says it was made from:";
}

/**
 * The shell: a modal that removes itself and hands focus back.
 *
 * `titleText` is set with `textContent` and referenced by `aria-labelledby`, so
 * the dialog is named without any markup being assembled from a string.
 */
function shell(
  id: string,
  className: string,
  titleText: string,
  opener: HTMLElement,
): { dialog: HTMLDialogElement; body: HTMLElement; dismiss: () => void } {
  const dialog = document.createElement("dialog");
  dialog.className = className;

  const title = document.createElement("h2");
  title.className = "compare-dialog-title";
  /*
   * A given id, not one derived from `className`. The parts dialog wears two
   * classes, so deriving produced `compare-dialog compare-dialog--parts-title`
   * — an id with a space in it, which `aria-labelledby` cannot resolve and
   * `querySelector` cannot select. The dialog rendered perfectly and was
   * nameless to a screen reader.
   */
  title.id = id;
  title.textContent = titleText;
  dialog.setAttribute("aria-labelledby", title.id);

  const body = document.createElement("div");
  body.className = "compare-dialog-body";

  dialog.append(title, body);
  document.body.append(dialog);

  /**
   * Close, detach, and hand focus back — **without depending on the `close`
   * event**.
   *
   * Measured in Chrome 150 on 2026-08-12: a **scripted** `close()` flips `open`
   * from true to false and fires no `close` event at all. A user-initiated
   * dismissal — Escape — goes through the UA's own path and does fire it, so
   * only the routes this module triggers itself were ever affected. Every
   * cleanup hung off that listener therefore never ran — the dialog stayed in
   * the document after being dismissed, so the next open would find a stale
   * subtree with live listeners still attached, and focus was left wherever the
   * click had put it instead of returning to the control that opened it.
   *
   * So the work happens where the dismissal is initiated, and the listener
   * stays as well for the paths the page does not initiate — Escape, and a
   * backdrop click.
   *
   * **The latch is load-bearing.** Both routes can run for one dismissal, and
   * relying on `close()` being a no-op on an already closed dialog to make that
   * harmless is a dependency on the environment: a `close()` that dispatches
   * unconditionally turns listener-calls-close into unbounded recursion.
   */
  let dismissed = false;
  const dismiss = (): void => {
    if (dismissed) return;
    dismissed = true;
    dialog.close();
    if (dialog.isConnected) dialog.remove();
    /*
     * A disabled opener cannot take focus, and silently does not — which is how
     * a keyboard user ends up back at the top of the document. The part control
     * is exactly that case: it is disabled while no model is loaded, which is
     * precisely when the first-choice dialog is open.
     */
    if (opener.isConnected && !(opener as HTMLButtonElement).disabled) {
      opener.focus();
    }
  };
  dialog.addEventListener("close", dismiss);

  return { dialog, body, dismiss };
}

/**
 * A dismissal that is a real button, not only the Escape key.
 *
 * Escape is the keyboard answer and native `<dialog>` gives it for free, but a
 * modal whose only visible exit is the backdrop leaves a pointer user guessing.
 *
 * Returned inside its own row, right-aligned, matching `.report-actions` — the
 * other dialog in this codebase and the desktop convention of putting a modal's
 * actions at the bottom right (owner, 2026-08-12). Two dialogs that place their
 * buttons differently make the second one a thing to read rather than a thing
 * already known.
 */
function closeButton(label: string, onClose: () => void): HTMLElement {
  const row = document.createElement("div");
  row.className = "compare-dialog-actions";
  const button = document.createElement("button");
  button.type = "button";
  button.className = "btn compare-dialog-dismiss";
  button.textContent = label;
  button.addEventListener("click", onClose);
  row.append(button);
  return row;
}

export interface SuggestionDialogOptions {
  readonly provenance: Provenance;
  /** Focus returns here on close. */
  readonly opener: HTMLElement;
  /** A suggestion was taken; the caller fills the field and focuses it. */
  readonly onPick: (repoId: string) => void;
  /** Dismissed — the caller opens the empty slot so a model can be typed. */
  readonly onDismiss: () => void;
}

/**
 * "Compare with…" → what this model says it came from.
 *
 * The caller must not open this when there is nothing to suggest: a modal whose
 * only content is a way out is worse than no modal. `provenance.claims` being
 * empty is the caller's cue to open the slot directly.
 */
export function openSuggestionDialog({
  provenance,
  opener,
  onPick,
  onDismiss,
}: SuggestionDialogOptions): HTMLDialogElement {
  const { dialog, body, dismiss } = shell(
    "compare-suggestion-title",
    "compare-dialog",
    "Compare with",
    opener,
  );

  const lead = document.createElement("p");
  lead.className = "compare-dialog-lead";
  lead.textContent = leadFor(provenance);
  body.append(lead);

  const list = document.createElement("div");
  list.className = "compare-dialog-choices";
  const shown = provenance.claims.slice(0, MAX_BASE_MODEL_CHIPS);
  const buttons: HTMLButtonElement[] = [];
  for (const claim of shown) {
    const choice = document.createElement("button");
    choice.type = "button";
    choice.className = "btn btn--primary compare-dialog-choice";

    const name = document.createElement("span");
    name.className = "compare-choice-name mono";
    name.textContent = claim.repoId;
    const source = document.createElement("span");
    source.className = "compare-choice-source";
    source.textContent = sourceLabel(claim.sources);
    choice.append(name, source);
    /*
     * The accessible name carries both lines. A button named only with the repo
     * id throws away the source label, which is half of what is being offered —
     * that a converter recorded it is a different claim from a README saying so.
     */
    choice.setAttribute(
      "aria-label",
      `Compare with ${claim.repoId} — ${sourceLabel(claim.sources)}`,
    );
    choice.addEventListener("click", () => {
      onPick(claim.repoId);
      dismiss();
    });
    buttons.push(choice);
    list.append(choice);
  }
  body.append(list);

  const hidden = provenance.claims.length - shown.length;
  if (hidden > 0) {
    /*
     * Said, not shown. A merge's lineage is plural and choosing which parents
     * reach the screen is already a choice; announcing that the list was cut is
     * the cheapest way to stop it reading as the whole truth.
     */
    const more = document.createElement("p");
    more.className = "compare-dialog-note";
    more.textContent = `and ${hidden} more not shown.`;
    body.append(more);
  }

  const caveat = document.createElement("p");
  caveat.className = "compare-dialog-note";
  /*
   * The revision caveat, and it is not pedantry.
   *
   * These declarations name a repo and never a commit, so a suggestion compares
   * against that repo's current `main` — which may have moved since this file
   * was made. Everything else in this app addresses an immutable SHA; this is
   * the one place a visitor is handed a moving target, so it says so.
   */
  caveat.textContent =
    "Compares against that repo's current main, which may have moved since this file was made.";
  body.append(caveat);

  body.append(
    closeButton("I'll choose myself", () => {
      onDismiss();
      dismiss();
    }),
  );

  dialog.showModal();
  // The recommendation, not the dismissal: the first button is the answer most
  // visitors want, and landing on "I'll choose myself" would put the way out
  // under the first Enter.
  buttons[0]?.focus();
  return dialog;
}

export interface PartDialogOptions {
  readonly components: readonly RepoComponent[];
  readonly kind: "models" | "quantizations";
  /**
   * What the dialog is for, since it serves two questions.
   *
   * "Compare against X" when B is being resolved before a comparison can run;
   * "Model A — X" when the part control is changing a side already on screen.
   * Same list, same behaviour, and a heading that lied about which would be
   * worse than no heading.
   */
  readonly title: string;
  readonly opener: HTMLElement;
  /** The chosen component's **label**, which is what `?part=` carries. */
  readonly onPick: (part: string) => void;
  readonly onDismiss: () => void;
  /**
   * Sizes and bit-widths, when they can be had — the same one-request
   * enrichment A's picker does.
   *
   * A promise rather than a value because the list must be on screen
   * immediately: "which of these is bigger" is worth waiting for, but not at
   * the cost of showing nothing while a metadata request is in flight. Resolves
   * to `undefined` when the request failed, and the labels stand on their own.
   */
  readonly enrich?: Promise<
    | {
        readonly components: readonly RepoComponent[];
        readonly summary: string | undefined;
        readonly excluded: readonly {
          readonly label: string;
          readonly bitsPerParam: number;
        }[];
      }
    | undefined
  >;
}

/**
 * B resolved to a repo holding several models — which one?
 *
 * A dialog rather than the full-screen picker A gets, because A's picker
 * *replaces the view* and B's question arrives with A already drawn on it.
 * Answering B's question by clearing A's map would discard the very thing the
 * comparison is against.
 */
export function openPartDialog({
  title,
  components,
  kind,
  opener,
  onPick,
  onDismiss,
  enrich,
}: PartDialogOptions): HTMLDialogElement {
  const { dialog, body, dismiss } = shell(
    "compare-part-title",
    "compare-dialog compare-dialog--parts",
    title,
    opener,
  );

  const inner = document.createElement("div");
  body.append(inner);

  /**
   * Draw the list and wire it. Called again if sizes arrive.
   *
   * `focusPart` restores the keyboard position across a redraw: a visitor
   * arrowing down the list when the sizes land must not be thrown back to the
   * top, and the list is *re-sorted* by width at that moment, so restoring an
   * index would land them on a different quantization than the one they were
   * on. The label is the identity that survives the sort.
   */
  function draw(
    list: readonly RepoComponent[],
    focusPart?: string,
    extra?: {
      readonly summary: string | undefined;
      readonly excluded: readonly {
        readonly label: string;
        readonly bitsPerParam: number;
      }[];
    },
  ): void {
    // The same renderer A's screen uses — sorted by width, sized where the file
    // list could say. Two pickers for one question would drift on the next
    // naming change; see `component-choice.ts`.
    // No headline: the dialog names itself, and `shell` has already pointed
    // `aria-labelledby` at that title. The rows are the shared thing.
    inner.innerHTML = renderComponentChoice(list, kind, {
      headline: false,
      summary: extra?.summary,
      excluded: extra?.excluded,
    });
    const buttons = [...inner.querySelectorAll<HTMLButtonElement>("[data-part]")];
    for (const button of buttons) {
      button.addEventListener("click", () => {
        const part = button.dataset.part;
        if (part === undefined) return;
        onPick(part);
        dismiss();
      });
    }
    const wanted =
      focusPart === undefined
        ? undefined
        : buttons.find((b) => b.dataset.part === focusPart);
    (wanted ?? buttons[0])?.focus();
  }

  draw(components);

  body.append(
    closeButton("Cancel", () => {
      onDismiss();
      dismiss();
    }),
  );

  void enrich?.then((better) => {
    // `isConnected` because the shell removes itself on close: a request that
    // outlives the dialog must not rebuild a detached subtree, and re-focusing
    // one would pull focus out of whatever the visitor moved on to.
    if (!better || !dialog.isConnected) return;
    const focused = document.activeElement;
    const held =
      focused instanceof HTMLElement && inner.contains(focused)
        ? focused.dataset.part
        : undefined;
    draw(better.components, held, {
      summary: better.summary,
      excluded: better.excluded,
    });
  });

  dialog.showModal();
  return dialog;
}
